import React, { FC, useEffect, useRef, useState } from "react";
import { Form, Formik, FormikHelpers } from "formik";
import * as Yup from "yup";
import dayjs from "dayjs";
import { useRouter } from "next/router";
import { useTranslations } from "next-intl";
import { useDispatch, useSelector } from "react-redux";
import { FlightSearchFormValues, Room, SearchFormValues } from "@/types/Search";
import { RootState } from "@/redux/types/RootState";
import { AppDispatch } from "@/redux/store";
import { addNotification } from "@/redux/reducer/notificationSlice";
import { useConfig } from "@/context/ConfigContext";
import DateRangePickerComp from "../Fields/DatePicker/DateRangePicker/DateRangePicker";
import CustomSearchField from "../Fields/CustomSearchField/CustomSearchField";
import AppButton from "@/components/buttons/AppButton/AppButton";
import {
  ClassIcon,
  CurrencyIcon,
  DepartingFlight,
  FlightDateIcon,
  ReturningFlight,
} from "@/components/icons";
import CustomSelectSearch from "../selects/SearchFormSelector/SearchFormSelector";
import SingleDatePickerComp from "../Fields/DatePicker/SingleDatePickerComp/SingleDatePickerComp";
import { flightLoadOptions } from "@/utils/flights/search/flightLoadOptions";
import FlightAutoSuggest from "../selects/FlightAutoSuggest/FlightAutoSuggest";
import FlightGuestsField, {
  Passengers,
} from "../Fields/selectFields/FlightGuestsField/FlightGuestsField";
import { searchFlights } from "@/utils/flights/search/fligitInitSearch";
import FlightGuestCabinSelect from "../selects/FlightGuestCabinSelect/FlightGuestCabinSelect";

interface FlightsFormProps { }

interface FormikError {
  [key: string]: string;
}
type DropdownType = "date" | "cabinClass";

const FlightsForm: FC<FlightsFormProps> = () => {
  const { config } = useConfig();
  const router = useRouter();
  const dispatch: AppDispatch = useDispatch();
  const searchState = useSelector((state: RootState) => state.hotelSearch);
  const t = useTranslations();


  const currency = useSelector(
    (state: RootState) => state.hotelSearch.currency || config.currency.code
  );
  const [loadingButton, setLoadingButton] = useState(false);


  const today = dayjs().startOf("day");
  const tomorrow = today.add(1, "day");
  const dayAfterTomorrow = tomorrow.add(1, "day");

  const [nationality, setNationality] = useState(
    searchState.nationality ? searchState.nationality : config.country.code
  );

  const [errorStyle, setErrorStyle] = useState(false);

  const [dropdowns, setDropdowns] = useState<Record<DropdownType, boolean>>({
    // nationality: false,
    date: false,
    cabinClass: false,
  });
  const [cabinClass, setCabinClass] = useState<string>("Economy");
  const [guestData, setGuestData] = useState<Passengers>({
    adults: 1,
    children: 0,
    infants: 0,
  });
  const calendarRef = useRef<HTMLDivElement>(null);
  const guestsRef = useRef<HTMLDivElement>(null);
  const dropdownRefs = {
    nationality: useRef<HTMLDivElement>(null),
    date: useRef<HTMLDivElement>(null),
  };

  const departureRef = useRef<{ focus: () => void }>(null);
  const destinationRef = useRef<{ focus: () => void }>(null);

  const handleSubmit = async (
    values: FlightSearchFormValues,
    { setSubmitting }: FormikHelpers<FlightSearchFormValues>
  ) => {
    try {
      setSubmitting(true);
      setLoadingButton(true);
      const segments: any[] = [
        {
          from: values.departure.value,
          to: values.destination.value,
        },
      ];
      if (values.tripType === "roundTrip") {
        segments.push({
          from: values.destination.value,
          to: values.departure.value,
        });
      }
      const payload = {
        preferences: {
          currency: "AED", // currency,
          lang: "en", //router.locale || 'en',
        },
        trip: values.tripType === "oneWay" ? "SINGLE" : "RETURN",
        segments: segments,
        departure_date: dayjs(values.date.startDate).format("YYYY-MM-DD"),
        return_date:
          values.tripType === "roundTrip" && values.date.endDate
            ? dayjs(values.date.endDate).format("YYYY-MM-DD")
            : "",
        travelClass: values.cabinClass,
        passengers: {
          adults: values.guests.adults,
          children: values.guests.children,
          infants: values.guests.infants,
        },
      };

      const response = await searchFlights(payload);
      if (response?.code === 200) {
        router.push(`/flights?sid=${response.response.sid}`, undefined, {
          locale: router.locale,
        });
      }
    } catch (err: any) {
      console.error("Flight search error:", err);
      dispatch(
        addNotification({
          type: "error",
          title: err.message || "Something went wrong",
          subtitle: t("error.Subtitle1"),
        })
      );
    } finally {
      setSubmitting(false);
      setLoadingButton(false);
    }
  };

  const handleDropdownToggle = (dropdown: DropdownType) => {
    setDropdowns((prev) => {
      const newDropdowns = {
        // nationality: dropdown === "nationality" ? !prev.nationality : false,
        date: dropdown === "date" ? !prev.date : false,
        cabinClass: dropdown === "cabinClass" ? !prev.cabinClass : false,
      };
      return newDropdowns;
    });
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const isClickOutside =
        !calendarRef.current?.contains(event.target as Node) &&
        !guestsRef.current?.contains(event.target as Node) &&
        // !dropdownRefs.nationality.current?.contains(event.target as Node) &&
        !dropdownRefs.date.current?.contains(event.target as Node);

      if (isClickOutside) {
        setDropdowns({
          // nationality: false,
          date: false,
          cabinClass: false,
        });
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const flightInitialValues: FlightSearchFormValues = {
    tripType: "oneWay",
    departure: {
      label: "",
      value: "",
      countryCode: "",
      country: "",
      CITY_NAME: "",
      NAME_SMALL_CAPS: "",
      city: "",
    },
    destination: {
      label: "",
      value: "",
      countryCode: "",
      country: "",
      CITY_NAME: "",
      NAME_SMALL_CAPS: "",
      city: "",
    },
    date: { startDate: tomorrow, endDate: dayAfterTomorrow },
    cabinClass: "E",
    guests: {
      adults: 1,
      children: 0,
      infants: 0,
    },
  };

  const FlightSchema = Yup.object().shape({
    tripType: Yup.string()
      .oneOf(["oneWay", "roundTrip"], "Trip type is required")
      .required("Trip type is required"),

    departure: Yup.object()
      .shape({
        label: Yup.string().required("Departure location is required"),
        value: Yup.string().required("Departure code is required"),
      })
      .required("Departure is required"),

    destination: Yup.object()
      .shape({
        label: Yup.string().required("Destination is required"),
        value: Yup.string().required("Destination code is required"),
      })
      .required("Destination is required"),

    cabinClass: Yup.string().required("Cabin class is required"),

    guests: Yup.object().shape({
      adults: Yup.number().min(1, "At least one adult is required").required(),
      children: Yup.number().min(0).required(),
      infants: Yup.number().min(0).required(),
    }),

    date: Yup.object()
      .shape({
        startDate: Yup.date().nullable().required("Start date is required"),
        endDate: Yup.mixed().nullable(),
      })
      .when("tripType", (tripType, schema) => {
        if (tripType && tripType[0] === "roundTrip") {
          return schema.shape({
            endDate: Yup.date().nullable().required("Return date is required"),
          });
        }
        return schema;
      }),
  });

  const cabinOptions = [
    { label: t("cabinClass.economy"), value: "E" },
    { label: t("cabinClass.premiumEconomy"), value: "P" },
    { label: t("cabinClass.business"), value: "B" },
    { label: t("cabinClass.first"), value: "F" },
  ];

  const handleGuestChange = (updatedGuests: Passengers) => {
    setGuestData(updatedGuests);
  };


  useEffect(() => {
    if (
      flightInitialValues.tripType === "roundTrip" &&
      flightInitialValues.date.startDate
    ) {
      const endDate = dayjs(flightInitialValues.date.startDate).add(1, "day");
      flightInitialValues.date.endDate = endDate;
    }
  }, [flightInitialValues.date.startDate, flightInitialValues.tripType]);

  const handleDateSelect = (date: dayjs.Dayjs | null) => {
    setDropdowns((prev) => ({
      ...prev,
      date: false,
    }));
  };

  return (
    <Formik
      initialValues={flightInitialValues}
      onSubmit={async (values, formikHelpers) => {
        const errors: FormikError = {};

        await FlightSchema.validate(values, { abortEarly: false }).catch(
          (err) => {
            err.inner.forEach((validationError: any) => {
              errors[validationError.path as keyof SearchFormValues] =
                validationError.message;
            });
          }
        );

        if (Object.keys(errors).length === 0) {
          await handleSubmit(values, formikHelpers);
          setErrorStyle(false);
        } else {
          formikHelpers.setErrors(errors);
          Object.keys(errors).forEach((key) => {
            dispatch(
              addNotification({
                type: "error",
                title: errors[key],
                subtitle: t(`error.Subtitle1`),
              })
            );
            setErrorStyle(true);
          });
        }
      }}
    >
      {({ setFieldValue, values, isSubmitting }) => (
        <Form className=" !w-full max-c-md:border-4 max-c-md:border-Goldenrod max-c-md:!rounded-3xl max-c-md:p-4">


          <div className="px-4 my-4 space-y-4">
            {/* <div
              role="group"
              aria-labelledby="trip-type-group"
              className="flex gap-6  "
            >
              <label className="flex items-center cursor-pointer">
                <input
                  type="radio"
                  name="tripType"
                  value="oneWay"
                  checked={values.tripType === "oneWay"}
                  onChange={() => {
                    setFieldValue("tripType", "oneWay");
                    setFieldValue("date.endDate", null);
                  }}
                  className="me-2 accent-primary"
                />
                {t("searchForm.oneWay")}
              </label>

              <label className="flex items-center cursor-pointer">
                <input
                  type="radio"
                  name="tripType"
                  value="roundTrip"
                  checked={values.tripType === "roundTrip"}
                  onChange={() => {
                    setFieldValue("tripType", "roundTrip");
                    if (!values.date.endDate) {
                      setFieldValue(
                        "date.endDate",
                        dayjs(values.date.startDate).add(1, "day")
                      );
                    }
                  }}
                  className="me-2 accent-primary"
                />
                {t("searchForm.roundTrip")}
              </label>
            </div> */}
            <div
  role="group"
  aria-labelledby="trip-type-group"
  className="flex gap-4"
>
  <button
    type="button"
    onClick={() => {
      setFieldValue("tripType", "oneWay");
      setFieldValue("date.endDate", null);
    }}
    className={`px-4 py-2 rounded-full border transition text-xs
      ${
        values.tripType === "oneWay"
          ? "bg-primary text-white border-primary"
          : "bg-white text-gray-700 border-gray-300 hover:bg-gray-100"
      }
    `}
  >
    {t("searchForm.oneWay")}
  </button>

  <button
    type="button"
    onClick={() => {
      setFieldValue("tripType", "roundTrip");
      if (!values.date.endDate) {
        setFieldValue(
          "date.endDate",
          dayjs(values.date.startDate).add(1, "day")
        );
      }
    }}
    className={`px-4 py-2 rounded-full border transition text-xs
      ${
        values.tripType === "roundTrip"
          ? "bg-primary text-white border-primary"
          : "bg-white text-gray-700 border-gray-300 hover:bg-gray-100"
      }
    `}
  >
    {t("searchForm.roundTrip")}
  </button>
</div>


            <div className=" mt-4 items-center gap-2 flex flex-row">
              <div className="relative flex flex-row w-6/12 ">
                <CustomSearchField
                  key="departure"
                  isSearchForm
                  icon={<DepartingFlight className="text-primary" />}
                  onClick={() => departureRef.current?.focus()}
                  name="departure"
                  className="!relative cursor-pointer w-full !rounded-e-none !rounded !border !border-[#d8dcde] !focus:outline-none "
                  error={errorStyle}
                  content={
                    <FlightAutoSuggest
                      ref={departureRef}
                      className="w-full"
                      name="departure"
                      placeholder={t("searchForm.departurePlaceholder")}
                      errorStyle={errorStyle}
                      value={values.departure}
                      loadOptions={flightLoadOptions}
                      onOptionSelect={(option) => {
                        setFieldValue("departure", option);
                        setErrorStyle(false);
                        if (option === null) {
                          setFieldValue("departure", { label: "", value: "" });
                        }
                      }}
                    />
                  }
                />
                <CustomSearchField
                  key="destination"
                  isSearchForm
                  icon={<ReturningFlight className="!text-[#000000de]" />}
                  onClick={() => destinationRef.current?.focus()}
                  name="destination"
                  className="!relative cursor-pointer w-full !rounded-s-none !rounded !border !border-[#d8dcde] !focus:outline-none"
                  error={errorStyle}
                  content={
                    <FlightAutoSuggest
                      ref={destinationRef}
                      className="w-full  "
                      name="destination"
                      placeholder={t("searchForm.flightsPlaceholder")}
                      errorStyle={errorStyle}
                      value={values.destination}
                      loadOptions={flightLoadOptions}
                      onOptionSelect={(option) => {
                        setFieldValue("destination", option);
                        setErrorStyle(false);
                        if (option === null) {
                          setFieldValue("destination", {
                            label: "",
                            value: "",
                          });
                        }
                      }}
                    />
                  }
                />
              </div>


              <div className="relative c-lg:w-3/12">
                <CustomSearchField
                  isSearchForm
                  icon={<FlightDateIcon className="text-primary" />}
                  name="date"
                  onClick={() => handleDropdownToggle("date")}
                  ref={dropdownRefs.date}
                  className="!relative cursor-pointer w-full !rounded !border !border-[#d8dcde] !focus:outline-none"
                  content={
                    values.tripType === "oneWay" ? (
                      <SingleDatePickerComp
                        selectedDate={values.date.startDate}
                        onChange={(date) => {
                          setFieldValue("date.startDate", date);
                          setDropdowns((prev) => ({
                            ...prev,
                            date: false,
                          }));
                        }}
                        dropdownIsOpen={dropdowns.date}
                      />
                    ) : (
                      <DateRangePickerComp
                        startDate={values.date.startDate}
                        endDate={values.date.endDate}
                        onChange={(start, end) => {
                          setFieldValue("date", {
                            startDate: start,
                            endDate: end
                          });
                        }}
                        dropdownIsOpen={dropdowns.date}
                      />
                    )
                  }
                />
              </div>
              <div className="relative c-lg:w-3/12 w-full">
                <FlightGuestCabinSelect
                onGuestChange={handleGuestChange}
                onCabinClassChange={setCabinClass}
                passengers={guestData}
                cabinClass={cabinClass}
                className="w-full h-14  !rounded !border !border-[#d8dcde]  flex items-center"
              />
                  {/* <FlightGuestsField
                    onGuestChange={(updatedGuests) => {
                      setFieldValue("guests", updatedGuests);
                    }}
                    passengers={values.guests}
                    className="w-full h-14 ps-10 !rounded-xl !border !border-[#c0cad5] flex items-center"
                  /> */}
             
                {/* <div className="relative c-lg:w-1/3">
                  <CustomSearchField
                    isSearchForm
                    icon={<ClassIcon className="text-primary" />}
                    name="cabinClass"
                    label={t("searchForm.cabinClass")}
                    className="!relative cursor-pointer w-full !rounded-xl !border !border-[#c0cad5]"
                    onClick={() => handleDropdownToggle("cabinClass")}
                    content={
                      <CustomSelectSearch
                        options={cabinOptions}
                        value={values.cabinClass}
                        onChange={(val) => {
                          setFieldValue("cabinClass", val);
                          setDropdowns((prev) => ({
                            ...prev,
                            cabinClass: false,
                          }));
                        }}
                        dropdownIsOpen={dropdowns.cabinClass}
                      />
                    }
                  />
                </div> */}
              </div>

              <AppButton
                variant="primary"
                label={t("searchForm.search")}
                className="flex w-[135px] h-14 bg-secondary  font-bold text-base !rounded"
                textClassName=" !text-base !text-white"
                type="submit"
                loading={loadingButton}
                disabled={loadingButton || isSubmitting}
              />
            </div>




          </div>


        </Form>
      )}
    </Formik>
  );
};

export default FlightsForm;
